import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  TokenService,
  AppInsightsService,
  CookieService,
  genTinyGUID,
  GoogleTagManagerService,
  SawDust
} from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { AdditionalAccountsCardState } from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { SubscriptionEmitService } from '@workflow/ecp-shared/service/subscription-emit/subscription-emit.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { BundleHelperService } from '@workflow/ecp-workflow/services/bundle-helper.service';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { getFlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowWidgetEventServiceService } from '@workflow/widget/workflow-widget-event.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { ProgressBarDataService } from '@workflow/dynamic-render/shared/workflow-progress/progress-bar.service';
import { StatefulService } from '@app/service/stateful.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { CapturedConsent } from '@workflow/ecp-shared/model/consent';
import { FlowService } from '@workflow/class/flow.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoOnClickFunctionsService {
  private logger = inject(SawDust).withContext({ component: 'OaoOnClickFunctionsService' });
  /**
   * Lender data configuration
   */
  public get lenderData(): LenderConfiguration {
    // Get the lender data configuration
    return this.lenderService.lenderData;
  }

  constructor(
    private readonly flowHelper: OaoPageHelper,
    private readonly userService: UserService,
    public readonly applicationApiService: ApplicationApiService,
    private readonly widgetEventService: WorkflowWidgetEventServiceService,
    private readonly bundleHelperService: BundleHelperService,
    public readonly subscribeEmitService: SubscriptionEmitService,
    private readonly router: Router,
    private readonly applicationInsightsService: AppInsightsService,
    private readonly storageWrapperService: StorageWrapperService,
    private readonly gtmService: GoogleTagManagerService,
    private readonly cookieService: CookieService,
    private readonly recaptcha: ReCaptchaV3Service,
    private readonly lenderService: LenderService,
    // Importing this as an easy/angular way to get product names
    // for the gtag push events
    private readonly progressBarDataService: ProgressBarDataService,
    private readonly tokenService: TokenService,
    private readonly statefulService: StatefulService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly stateManagementService: StateManagementService,
    private readonly flowService: FlowService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {}

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.applicationInsightsService.logEvent(eventName, eventData);
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * setOAOMembership
   */
  public setOAOMembership = (context: LogicFnContext) => {
    const correlationId = genTinyGUID(24);
    this.logEvent('ecp.ui.getMembershipId', {
      membershipId: null,
      eventState: 'start',
      operationName: 'setOAOMembership',
      correlationId
    });
    let bundleMembershipID = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    const membershipId = this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value;
    const dropdownMembershipId = this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value;
    const memMembershipID = membershipId || dropdownMembershipId;
    this.logEvent('ecp.ui.getMembershipId', {
      membershipId,
      eventState: 'end',
      operationName: 'setOAOMembership',
      correlationId,
      status: membershipId ? 'success' : 'error'
    });
    if (!this.userService.isAuthenticated || bundleMembershipID == null) {
      this.flowHelper.SetOAOMembership(memMembershipID.toString(), context.flow.id);
      bundleMembershipID = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    }

    if (this.userService.isAuthenticated && memMembershipID.toString() !== bundleMembershipID.toString()) {
      this.statefulService.wrap('ss:showMembershipChangeModal').value = true;
      return;
    }

    // Added null check because confirmMembershipComponent sends and object like this {field: someField}. The field contains flow data,
    // but there is no flow key. Should a flow be sent in instead?
    if (context.flow?.id === 'oao') {
      this.statefulService.wrap('ss:oao-first-login').value = true;
    }
    this.userService.flowType = 'OAO';

    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    const redirectURL = this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').valueString;
    const flow = this.flowService.createFlow(redirectURL || `oao/${membershipTypeName.toLowerCase()}`);
    if (flow.config.streamlined) {
      this.workflowLoader.start('Connecting to your profile...', {
        animationId: 'documentscan',
        overlayMode: 'card',
        segments: this.flowHelper.flow.config?.streamlinedSkipPages?.length
      });
    } else {
      this.logger.warn('hit end lacking streamlined mode');
      this.workflowLoader.hide();
    }
    // If It is business membership then make sure atleast one responsible party in the bundle to taggele econsent splash screen.'
    if (membershipTypeName.toLowerCase() === 'business') {
      this.updateBusinessPrimaryConsents();
    }

    let firstVisiblePage = '';
    const visiblePages = flow.pages.filter((t) => t.visible && t.id !== 'oao-membership');
    if (visiblePages && visiblePages.length > 0) {
      const flowInfo = getFlowInfo(flow);
      firstVisiblePage = `${flowInfo.configPath}/${visiblePages[0].path}`;
      this.defaultNavigation(context);
      this.router.navigateByUrl(firstVisiblePage);
    }
  };

  /** Method to initialize ReponsibleParties and update eConsent Data when user choose business membership */
  public updateBusinessPrimaryConsents = () => {
    const primaryConsent = this.adminPortalFeaturesService?.config?.consentManagement?.primaryAcknowledgement;
    // Add default primary responsible party object in the bundle when econsent splash screen is enabled
    if (!primaryConsent?.enableConsentScreen) {
      return;
    }
    const primaryAdditionalConsentPath = 'api:oaoMemberInformation/primaryApplicant/additionalConsents';
    const businessRootPath = `api:oaoMemberInformation/businessInformation/responsibleParties`;
    const businessAdditionalConsentPath = `${businessRootPath}/0/additionalConsents`;
    // Add a responsible parties as default if none exists.
    if (this.statefulService.arraySize(businessRootPath) === 0) {
      this.statefulService.arrayAdd(`${businessRootPath}`);
    }

    const primaryConsentisAccpeted = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/consent/isAccepted').value;

    this.statefulService.wrap(`${businessRootPath}/0/consent/isAccepted`).value = primaryConsentisAccpeted;
    const primaryAdditionalConsents = this.stateManagementService.getValue<CapturedConsent[]>(primaryAdditionalConsentPath) || [];
    let arraySize = this.statefulService.arraySize(businessAdditionalConsentPath, 0) || 0;
    // Add AdditionalConsents to responsible parties
    while (arraySize < primaryAdditionalConsents.length) {
      this.statefulService.arrayAdd(businessAdditionalConsentPath);
      arraySize++;
    }
    primaryAdditionalConsents.forEach((t, index) => {
      this.statefulService.wrap(`${businessAdditionalConsentPath}/${index}/isAccepted`).value = t.isAccepted;
      this.statefulService.wrap(`${businessAdditionalConsentPath}/${index}/acceptanceText`).value = t.title;
      this.statefulService.wrap(`${businessAdditionalConsentPath}/${index}/acknowledgeId`).value = t.acknowledgeId;
      this.statefulService.wrap(`${businessAdditionalConsentPath}/${index}/agreedDate`).value = t.agreedDate;
      this.statefulService.wrap(`${businessAdditionalConsentPath}/${index}/acceptanceSnapshotReferenceId`).value =
        t.acceptanceSnapshotReferenceId;
    });
  };

  public setBusinessMembership = (): void => {
    this.flowHelper.setBusinessMembership();
  };

  public navigateToPrevious = (): void => {
    if (this.userService && this.userService.isAuthenticated) {
      this.router.navigate(['/consumer/dashboard']);
    } else {
      this.router.navigate(['/user/login']);
    }
  };

  public navigateToLogin = (): void => {
    this.router.navigate(['/user/login']);
  };

  public savePreciseIDAnswers = (context: LogicFnContext): void => {
    if (context && context.page) {
      this.flowHelper.savePreciseIDAnswers();
    }
  };

  public clearOAOApplicationDataForManual = () => {
    this.flowHelper.clearOAOApplicationDataForManual();
  };

  public submitCompareAndFraud = (context: LogicFnContext): void => {
    this.statefulService.wrap('api:oaoMemberInformation/hasApplicationCheckpointPass').value = true;
    this.flowHelper.submitCompareAndFraud();
    this.goToOAOIncompletePage(context);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public clearAlertMessage = (context: LogicFnContext): void => {
    this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = null;
  };

  /**
   * Submits an Online Account Opening (OAO) application.
   *
   * @remarks
   * This function triggers a series of operations: application submission, logging and notification events,
   * and updating the application flow.
   *
   * @param context - Provides the context of the logic function, including page and flow information.
   */
  public submitOAOApplication = (context: LogicFnContext): void => {
    // Generate a unique identifier for the submit confirmation event
    const submitConfirmationEventId = genTinyGUID(24);

    // Generate an event pair identifier for the ola submit event
    const submitEventId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated

    // Broken out logic for Cognitive Compelxity Decrease
    this.initializeOAOSubmit(context, submitConfirmationEventId);

    // Save the changes in the stateful values
    // Fix for Tech Story 255969: Disclosure Acceptance should map as Yes (true) when submitted to arcOS
    this.applicationApiService.patchChangedValues('oao', (isSuccess, error) => {
      if (isSuccess) {
        this.logger.debug('Patch values are saved successfully on submit.');
      } else {
        this.logger.withError(error).warn('Unable to save OAO patch value on submit.');
      }
      this.processOAOSubmit(context, submitEventId, submitConfirmationEventId);
    });
  };

  private processOAOSubmit(context: LogicFnContext, submitEventId: string, submitConfirmationEventId: string): void {
    // Using one of the common ID's from the bundle to make a GET call
    this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').load(() => {
      // Indicate that an API call is in progress
      this.statefulService.wrap('mem:isAPICallProgress').value = true;
      this.applicationApiService.submitResult = false;

      // // Retrieve the user email and application number
      // TODO: We seem to be getting/setting these in odd places in this file, should be refactored
      const logUser = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString ?? 'Unknown';
      const logAppNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString ?? 'Unknown';

      // Log the submit start event
      this.logAppInsightsOAOSubmitEvent(submitEventId, 'start', logAppNumber, logUser);

      // Submit the OAO application
      this.applicationApiService.submitOAOApplication((isSuccess: boolean, err = null) => {
        // Indicate that the action button is no longer loading
        context.page.actionButtonIsLoading = false;
        // Refresh the bundle
        this.bundleHelperService.refreshBundle(() => {
          // Manually emit event about application successfully submitted to notify Postapp
          this.subscribeEmitService.emit(Info.oaoSubmitListener, isSuccess ? Info.submitted : Info.failed);
          return;
        });

        // Log the end of the submit event
        this.logAppInsightsOAOSubmitEvent(
          submitEventId,
          'end',
          logAppNumber,
          logUser,
          isSuccess ? 'success' : 'error',
          isSuccess ? null : JSON.stringify(err)
        );

        if (!isSuccess) {
          // Log the end of the submit confirmation event in case of failure or trigger a 'SubmitOAOApplication' event in case of success
          this.logEvent('ecp.ui.oaoSubmitUiConfirmation', {
            eventState: 'end',
            correlationId: submitConfirmationEventId,
            status: 'error'
          });
        } else {
          const applicationId = this.storageWrapperService.get<string>(Info.appNumber);

          // const entityId = this.statefulService.wrap('oao:entityId').valueString;
          const entityId = this.statefulService.wrap('api:entityId').value;
          // const entityId = this.storageWrapperService.get<string>('oao:entityId');
          this.gtmService.publishEvent('SubmitOAOApplication', {
            ApplicationId: applicationId,
            ArcDXApplicationID: entityId,
            ProductName: this.progressBarDataService?.productName
          });
          this.gtmService.publishEvent('arcdx.accounts.submit', {
            ApplicationId: applicationId,
            ArcDXApplicationID: entityId,
            ProductName: this.progressBarDataService?.productName
          });
        }

        // Indicate that the API call has finished
        this.statefulService.wrap('mem:isAPICallProgress').value = false;
      });
    });

    // Break off post-submission tasks to reduce cognitivie complexity
    this.finalizeOAOSubmit(context);
  }

  /**
   * Initial steps for submitting an Online Account Opening (OAO) application.
   *
   * This method triggers the preliminary tasks required for submitting an OAO application. It checks
   * if the page is complete, logs the initial application submit event, and modifies the page state
   * to indicate that the submission process is ongoing.
   *
   * @param context - Provides the context of the logic function, including page and flow information.
   * @param oaoSubmitConfId - Unique identifier for the submit confirmation event.
   */
  private initializeOAOSubmit(context: LogicFnContext, oaoSubmitConfId: string): void {
    // Prevent further execution if the page is not complete
    if (!context.page.helper.isPageComplete) return;

    this.logEvent('ecp.ui.oaoSubmitUiConfirmation', {
      eventState: 'start',
      correlationId: oaoSubmitConfId
    });

    // Store the event ID in the session storage
    sessionStorage.setItem('oaoSubmitEventID', oaoSubmitConfId);

    // Indicate that an action button is loading
    context.page.actionButtonIsLoading = true;
  }

  /**
   * Finalizes the Online Account Opening (OAO) application submission process.
   *
   * @param context - Provides the context of the logic function, including page and flow information.
   */
  private finalizeOAOSubmit(context: LogicFnContext): void {
    // Update the user for the OAO post application
    this.applicationApiService.setUserForOaoPostApp();

    // Set the OAO flow
    this.flowHelper.setOaoFlow();

    // Retrieve the loan number
    const loanNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;

    // Store the loan number
    this.storageWrapperService.set(Info.appNumber, loanNumber);

    // Emit an event indicating the application has been submitted
    this.widgetEventService.emitApplicationSubmit({ loanNumber, flowId: context.flow.id });
  }

  /**
   * Logs an event in Application Insights, typically called when an account submission process either starts or ends.
   * This method is specifically meant for tracking the status and associated details of each loan submission attempt.
   *
   * @param correlationId - Unique identifier to correlate events in a single transaction.
   * @param eventState - Represents the state of the event, whether it's 'start' or 'end'.
   * @param appNumber - Unique identifier of the account which is being processed.
   * @param user - Identifier for the user triggering this event, could be username or user ID.
   * @param status - Optional parameter that captures the status of the event. It can either be 'success' or 'error'.
   * @param errorDetails - Optional parameter that captures the details of the error, if any.
   *
   * @returns This method doesn't have a return.
   */
  private logAppInsightsOAOSubmitEvent(
    correlationId: string,
    eventState: 'start' | 'end',
    appNumber: string,
    user: string,
    status: 'success' | 'error' = null,
    errorDetails: string = null
  ): void {
    const source = 'workflow'; // we also track this event in the arcDX UI for Post-App resubmit
    const domain = window.location.origin; // TODO: SHOULD BE DEFAULT IN ALL LOGS
    this.logEvent('ecp.ui.oaoSubmit', {
      eventState,
      correlationId,
      status,
      appNumber,
      user,
      domain,
      source,
      errorDetails
    });
  }

  private defaultNavigation(context: LogicFnContext): void {
    if (context && context.defaultNavigationFn) {
      context.defaultNavigationFn();
    }
  }

  /**
   * This method traverse to incomplete OAO page
   * @param context
   */
  public goToOAOIncompletePage = (context: LogicFnContext): void => {
    context?.defaultNavigationFn && context.defaultNavigationFn();
  };

  private isExtradataOAOCompleted(id: string, extraData: string): boolean {
    if (!extraData) {
      return false;
    }

    const extraDataObj = JSON.parse(extraData);
    return !extraDataObj[id] ||
      !extraDataObj[id].isVisited ||
      !extraDataObj[id].state ||
      extraDataObj[id].state.toLowerCase() !== 'completed'
      ? false
      : true;
  }

  // Sets a cookie to indicate the authentication type used for the application. This is read later
  // in arcDX and fed into Login Widget.
  private handleAuthTypeCookie(context: LogicFnContext, apiPrimaryRoot: string): void {
    const mobile = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).value;
    const isPersonalInfo = context.page.id === 'oao-application-fields';
    const isUserLoggedIn = this.userService.isAuthenticated;
    if (mobile && isPersonalInfo && !isUserLoggedIn) {
      this.cookieService.setCookie('arcDXAuthType', 'phone', 365, '/');
      this.cookieService.setCookie('arcDXReturningUser', 'true', 90, '/');
    } else if (!mobile && isPersonalInfo && !isUserLoggedIn) {
      this.cookieService.setCookie('arcDXAuthType', 'email', 365, '/');
      this.cookieService.setCookie('arcDXReturningUser', 'true', 90, '/');
    }
  }

  /**
   * This method used to regsiter the user and next to incomplete OAO page
   * @param context
   */
  public savePersonalInfo = (context: LogicFnContext): void => {
    const { isBusiness, apiPrimaryRoot } = this.getPrimaryRoot();
    this.handleAuthTypeCookie(context, apiPrimaryRoot);

    /** check whether the user already registered */
    this.tokenService.configureCache('sessionStorage', 'ecp-user-session');
    const isRegisteredApplication = this.statefulService.wrap('api:isRegisteredApplication').value;
    const isAuthenticated = this.userService.isAuthenticated;
    if (isRegisteredApplication || isAuthenticated) {
      const cachedToken = this.tokenService.getCachedToken();
      let tokenType = 'unknown';
      if (cachedToken) {
        tokenType = this.tokenService.determineTokenType();
      }

      if (cachedToken && tokenType === 'arcDX') {
        this.silentRegisterWithRecaptcha(context, isBusiness, true);
      } else {
        this.goToOAOIncompletePage(context);
        this.setValueToCondense(isBusiness);
      }
    } else {
      this.silentRegisterWithRecaptcha(context, isBusiness);
    }
  };

  public getPrimaryRoot() {
    let isBusiness = false;
    // 1. identify Membership Type 1. Personal = 1, 2. Youth = 2, 3. Business = 3,
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    let apiPrimaryRoot = 'api:oaoMemberInformation/primaryApplicant';
    if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
      apiPrimaryRoot = 'api:oaoMemberInformation/businessInformation/responsibleParties/0';
      isBusiness = true;
    }

    return { isBusiness, apiPrimaryRoot };
  }

  public getRegisterPayload() {
    const { apiPrimaryRoot } = this.getPrimaryRoot();
    const email = this.statefulService.wrap(`${apiPrimaryRoot}/email`).valueStringOnly?.toLowerCase();
    const cellPhone = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).valueStringOnly;
    const formattedCellPhone = cellPhone ? cellPhone.replace(/\D/g, '') : cellPhone;
    return {
      email,
      confirmEmail: email,
      isRegisteredWithCuid: false,
      registeredWithSSO: false,
      loginType: 'OAO',
      cellPhone: formattedCellPhone,
      firstName: this.statefulService.wrap(`${apiPrimaryRoot}/firstName`).valueStringOnly,
      lastName: this.statefulService.wrap(`${apiPrimaryRoot}/lastName`).valueStringOnly,
      isSilentRegistration: true
    };
  }
  /**
   * This method used to regsiter the user with recaptcha verification and next to incomplete page
   * @param context
   */
  private readonly silentRegisterWithRecaptcha = (context: LogicFnContext, isBusiness, isInternal = false): Promise<void> => {
    const postData = this.getRegisterPayload();
    const { apiPrimaryRoot } = this.getPrimaryRoot();

    return new Promise((resolve, reject) => {
      if (
        this.lenderData.enableRecaptcha &&
        this.statefulService.wrap(`${apiPrimaryRoot}/autoFillOption`).valueString !== 'Prefill' &&
        !isInternal
      ) {
        this.recaptcha.execute('silent_register').subscribe({
          next: (token) => {
            this.silentRegister(context, isBusiness, postData, token).then(resolve, reject);
          },
          error: () => {
            this.statefulService.wrap('ss:showMFAVerification').value = true;
            reject();
          }
        });
      } else {
        this.silentRegister(context, isBusiness, postData).then(resolve, reject);
      }
    });
  };
  /**
   * This method used to regsiter the user and next to incomplete OAO page
   * @param context
   */
  private readonly silentRegister = (context: LogicFnContext, isBusiness, postData, token?: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const user = this.userService.user || ({} as UserInfo);
      user.email = postData.email;
      user.cellPhone = postData.cellPhone;
      user.mfaVerifyMethod = user.cellPhone ? 'Phone' : 'Email_Code';
      this.userService.user = user;
      this.applicationApiService.silentRegistration(
        postData,
        token,
        (response) => {
          if (!response.isSuccess) {
            // Person is high risk, show MFA verification
            this.statefulService.wrap('ss:showMFAVerification').value = true;
            reject(response);
            return;
          }
          this.userService.isExistingUser = true;

          if (!!token) {
            this.statefulService.wrap('api:isRecaptchaVerified').value = true;
          }
          this.statefulService.wrap('api:isRegisteredApplication').value = true;
          this.statefulService.wrap('ss:trigger-appId-creation-event').value = true; // Triggers GA event once appId available
          // this.SetUser(response);
          this.goToOAOIncompletePage(context);
          this.setValueToCondense(isBusiness, this.flowHelper.flow?.config?.streamlined);
          resolve();
        },
        () => {
          this.setValueToCondense(isBusiness, this.flowHelper.flow?.config?.streamlined);
          reject();
        }
      );
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isAdditionalAccountSelected = (context: LogicFnContext): void => {
    const additionalAccountSharesCount = this.statefulService.arraySize(
      'api:oaoMemberInformation/accountsAndServices/primaryApplicant/shares'
    );
    const showFullList = this.statefulService.wrap('ss:isAdditionalAccountCardExpanded').value as boolean;
    let additionalAccountsCardState: AdditionalAccountsCardState;
    if (additionalAccountSharesCount > 0) {
      additionalAccountsCardState = {
        isCardSaved: true,
        lastSavedState: showFullList
      };
      this.statefulService.wrap('api:oaoMemberInformation/additionalAccountCardState/isCardSaved').value = true;
      this.statefulService.wrap('api:oaoMemberInformation/additionalAccountCardState/lastSavedState').value = showFullList;
    }
    this.statefulService.wrap('ss:AdditionalAccountCardState').value = JSON.stringify(additionalAccountsCardState);
  };

  public setValueToCondense(isBusiness: boolean, isStreamlined: boolean = false) {
    if (isBusiness) {
      this.statefulService.wrap('mem:edit-oao-business-res-personal-preview-#', null, 0).value = false;
    } else if (!isStreamlined) {
      this.statefulService.wrap('mem:edit-oao-primary-personal-preview').value = false;
      this.statefulService.wrap('mem:edit-oao-youth-primary-personal-preview').value = false;
    }
  }
}
